import { auth } from '../lib/firebase';

export interface QRPromptPayResponse {
  success: boolean;
  source_id: string;
  qr_code_url: string;
  amount: number;
  currency: string;
  expires_at?: string;
}

export interface PaymentVerificationResponse {
  success: boolean;
  status: 'pending' | 'successful' | 'failed' | 'expired';
  charge_id?: string;
  paid_at?: string;
  message?: string;
}

/**
 * สร้าง QR PromptPay สำหรับการชำระเงิน
 * @param amount จำนวนเงินเป็นบาท (จะถูกแปลงเป็น satangs ใน function)
 * @returns QR Code URL และ Source ID สำหรับตรวจสอบสถานะ
 */
export const generateQRPromptPay = async (amount: number): Promise<QRPromptPayResponse> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('กรุณาเข้าสู่ระบบก่อนทำการชำระเงิน');
  }
  
  // Force refresh token to ensure it's valid
  console.log('[QR PromptPay] Refreshing authentication token...');
  const token = await user.getIdToken(true);
  
  console.log('[QR PromptPay] Creating QR Code for amount:', amount);
  console.log('[QR PromptPay] User ID:', user.uid);
  
  // Use the correct Cloud Function URL (deployed from functions-standalone)
  const response = await fetch('https://us-central1-edumi-4dfbe.cloudfunctions.net/createPromptPayQR', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      amount: amount * 100, // แปลงเป็น satangs
    }),
  });

  if (!response.ok) {
    let errorMessage = 'ไม่สามารถสร้าง QR Code ได้';
    try {
      const errorData = await response.json();
      errorMessage = errorData.error || errorMessage;
      
      // Specific error messages
      if (response.status === 401 || response.status === 403) {
        errorMessage = 'การยืนยันตัวตนล้มเหลว กรุณาเข้าสู่ระบบใหม่';
      } else if (response.status === 500) {
        errorMessage = 'เกิดข้อผิดพลาดจากเซิร์ฟเวอร์ กรุณาลองใหม่อีกครั้ง';
      }
    } catch (e) {
      console.error('[QR PromptPay] Error parsing error response:', e);
    }
    
    console.error('[QR PromptPay] Failed to create QR code:', response.status, errorMessage);
    throw new Error(errorMessage);
  }

  const data = await response.json();
  console.log('[QR PromptPay] QR Code created successfully:', data.source_id);

  return data;
};

/**
 * ตรวจสอบสถานะการชำระเงินผ่าน QR PromptPay
 * @param sourceId Source ID ที่ได้จากการสร้าง QR Code
 * @returns สถานะการชำระเงิน
 */
export const verifyQRPayment = async (sourceId: string): Promise<PaymentVerificationResponse> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('กรุณาเข้าสู่ระบบก่อนทำการชำระเงิน');
  }
  
  // Force refresh token to ensure it's valid
  const token = await user.getIdToken(true);
  
  console.log('[QR PromptPay] Verifying payment for source:', sourceId);
  
  // Use the correct Cloud Function URL (deployed from functions-standalone)
  const functionUrl = 'https://us-central1-edumi-4dfbe.cloudfunctions.net/verifyPromptPayPayment';
  
  const response = await fetch(functionUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      source_id: sourceId,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Failed to verify payment');
  }

  const data = await response.json();
  console.log('[QR PromptPay] Payment verification result:', data.status);

  return data;
};

/**
 * Polling สำหรับตรวจสอบสถานะการชำระเงินอัตโนมัติ
 * @param sourceId Source ID ที่ได้จากการสร้าง QR Code
 * @param onStatusChange Callback เมื่อสถานะเปลี่ยน
 * @param maxAttempts จำนวนครั้งสูงสุดในการตรวจสอบ (default: 60 = 15 นาที)
 * @param intervalMs ระยะเวลาระหว่างการตรวจสอบ (default: 15000 = 15 วินาที)
 * @returns Function สำหรับหยุด polling
 */
export const startPaymentPolling = (
  sourceId: string,
  onStatusChange: (status: PaymentVerificationResponse) => void,
  maxAttempts: number = 60,
  intervalMs: number = 15000
): (() => void) => {
  let attempts = 0;
  let intervalId: NodeJS.Timeout | null = null;
  let stopped = false;

  const checkPayment = async () => {
    if (stopped) return;
    
    attempts++;
    console.log(`[QR PromptPay] Polling attempt ${attempts}/${maxAttempts}`);

    try {
      const result = await verifyQRPayment(sourceId);
      
      // เรียก callback ทุกครั้งที่ตรวจสอบ
      onStatusChange(result);

      // หยุด polling ถ้าชำระเงินสำเร็จ, ล้มเหลว, หรือหมดอายุ
      if (result.status === 'successful' || result.status === 'failed' || result.status === 'expired') {
        console.log('[QR PromptPay] Payment finalized with status:', result.status);
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
        return;
      }

      // หยุด polling ถ้าเกินจำนวนครั้งที่กำหนด
      if (attempts >= maxAttempts) {
        console.log('[QR PromptPay] Max polling attempts reached');
        onStatusChange({
          success: false,
          status: 'expired',
          message: 'QR Code หมดอายุ กรุณาสร้าง QR Code ใหม่',
        });
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
      }
    } catch (error) {
      console.error('[QR PromptPay] Polling error:', error);
      // ไม่หยุด polling เมื่อเกิด error เพื่อให้ลองใหม่อัตโนมัติ
    }
  };

  // เริ่ม polling
  console.log('[QR PromptPay] Starting payment polling...');
  checkPayment(); // ตรวจสอบครั้งแรกทันที
  intervalId = setInterval(checkPayment, intervalMs);

  // Return function สำหรับหยุด polling
  return () => {
    stopped = true;
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
    console.log('[QR PromptPay] Polling stopped');
  };
};
